import {
    Box, Typography, Button, Modal, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Skeleton, Avatar, Select,
    MenuItem, FormControl, InputLabel, ToggleButton, ToggleButtonGroup,
    Checkbox, FormControlLabel, Alert
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import AddIcon from '@mui/icons-material/Add';
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const REQUEST_TYPES = [
    { value: 'leave', label: 'Leave' },
    { value: 'comp_off', label: 'Comp-Off' },
    { value: 'on_duty', label: 'On-Duty' },
    { value: 'permission', label: 'Permission' },
];

const LEAVE_CATEGORIES = [
    { value: 'casual', label: 'Casual (CL)' },
    { value: 'sick', label: 'Sick (SL)' },
    { value: 'earned', label: 'Earned (EL)' },
    { value: 'lop', label: 'Loss of Pay' },
];

const TYPE_STYLE = {
    leave: { bg: '#FFF1F0', border: '#F5222D', color: '#F5222D' },
    comp_off: { bg: '#F6FFED', border: '#52C41A', color: '#52C41A' },
    on_duty: { bg: '#E6F4FF', border: '#0071BC', color: '#0071BC' },
    permission: { bg: '#FFF7E6', border: '#FA8C16', color: '#FA8C16' },
};

const STATUS_STYLE = {
    pending: { bg: '#FAFAFA', border: '#BFBFBF', color: '#6B7280' },
    approved: { bg: '#F6FFED', border: '#52C41A', color: '#52C41A' },
    rejected: { bg: '#FFF1F0', border: '#F5222D', color: '#F5222D' },
    cancelled: { bg: '#FAFAFA', border: '#BFBFBF', color: '#6B7280' },
};

const EMPTY_FORM = {
    staff_id: '',
    request_type: 'leave',
    leave_category: 'casual',
    from_date: dayjs().format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
    from_time: '',
    to_time: '',
    is_half_day: false,
    reason: '',
};

function Chip({ style, label }) {
    return (
        <Box sx={{
            display: 'inline-flex', justifyContent: 'center',
            backgroundColor: style.bg, border: '1px solid', borderColor: style.border,
            borderRadius: '2px', padding: '1px 8px', whiteSpace: 'nowrap'
        }}>
            <Typography sx={{ fontSize: '11px', lineHeight: '20px', color: style.color, textTransform: 'uppercase' }}>
                {label}
            </Typography>
        </Box>
    );
}

function StatCard({ label, value, hint, accent }) {
    return (
        <Box sx={{
            flex: 1, minWidth: '150px', backgroundColor: 'background.light',
            borderRadius: '12px', padding: '13px 16px',
            boxShadow: '0px 0px 25px 0px #00000012',
            borderLeft: '3px solid', borderColor: accent
        }}>
            <Typography sx={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.07em', color: 'text.secondary' }}>{label}</Typography>
            <Typography sx={{ fontSize: '22px', fontWeight: 600, lineHeight: 1.25 }}>{value}</Typography>
            <Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>{hint}</Typography>
        </Box>
    );
}

function initialsOf(name) {
    if (!name) return '--';
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

export function StaffRequests() {
    const selectedHub = useSelector(state => state.user.selected_hub);
    const toast = useRef(null);

    const [requests, setRequests] = useState([]);
    const [staffOptions, setStaffOptions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [range, setRange] = useState([dayjs().startOf('month').toDate(), dayjs().endOf('month').toDate()]);

    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [quote, setQuote] = useState(null);
    const [quoteError, setQuoteError] = useState(null);

    const hubId = useMemo(() => {
        const normalize = (value) => {
            if (value === null || value === undefined) return null;
            if (Array.isArray(value)) return normalize(value[0]);
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        };
        const fromStore = normalize(selectedHub);
        if (fromStore) return fromStore;
        const stored = localStorage.getItem('selected_hub');
        if (!stored) return null;
        try {
            return normalize(JSON.parse(stored));
        } catch (e) {
            return normalize(stored);
        }
    }, [selectedHub]);

    const showToast = (severity, summary, detail) => {
        if (toast.current) toast.current.show({ severity, summary, detail, life: 4000 });
    };

    const errorMessage = (error, fallback) => {
        const detail = error?.response?.data?.error;
        if (!detail) return fallback;
        if (typeof detail === 'string') return detail;
        const firstKey = Object.keys(detail)[0];
        const firstValue = detail[firstKey];
        return Array.isArray(firstValue) ? firstValue[0] : String(firstValue);
    };

    useEffect(() => {
        if (!hubId) return;
        fetchRequests();
        fetchStats();
        fetchStaff();
    }, [hubId]);

    useEffect(() => {
        if (!hubId) return;
        fetchRequests();
    }, [typeFilter, statusFilter, range]);

    const fetchStaff = async () => {
        try {
            const res = await CustomAxios.get('staff/options/', { params: { hub_id: hubId } });
            setStaffOptions(res.data || []);
        } catch (error) {
            setStaffOptions([]);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await CustomAxios.get('staff/requests/stats/', { params: { hub_id: hubId } });
            setStats(res.data);
        } catch (error) {
            setStats(null);
        }
    };

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const params = { hub_id: hubId };
            if (typeFilter) params.type = typeFilter;
            if (statusFilter) params.status = statusFilter;
            if (Array.isArray(range) && range[0]) {
                params.from = dayjs(range[0]).format('YYYY-MM-DD');
                params.to = dayjs(range[1] || range[0]).format('YYYY-MM-DD');
            }
            const res = await CustomAxios.get('staff/requests/', { params });
            setRequests(res.data || []);
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to load requests'));
        } finally {
            setLoading(false);
        }
    };

    // Live cost preview. Debounced because it fires on every date/type keystroke.
    useEffect(() => {
        if (!modalOpen || !formData.staff_id) {
            setQuote(null);
            setQuoteError(null);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const body = {
                    staff_id: formData.staff_id,
                    request_type: formData.request_type,
                    leave_category: formData.request_type === 'leave' ? formData.leave_category : null,
                    from_date: formData.from_date,
                    to_date: formData.request_type === 'permission' ? formData.from_date : formData.to_date,
                    is_half_day: formData.is_half_day,
                };
                if (formData.request_type === 'permission') {
                    if (!formData.from_time || !formData.to_time) {
                        setQuote(null);
                        setQuoteError(null);
                        return;
                    }
                    const [fh, fm] = formData.from_time.split(':').map(Number);
                    const [th, tm] = formData.to_time.split(':').map(Number);
                    const minutes = (th * 60 + tm) - (fh * 60 + fm);
                    if (minutes <= 0) {
                        setQuote(null);
                        setQuoteError('End time must be after the start time.');
                        return;
                    }
                    body.hours_count = (minutes / 60).toFixed(1);
                }
                const res = await CustomAxios.post('staff/requests/quote/', body);
                setQuote(res.data);
                setQuoteError(null);
            } catch (error) {
                setQuote(null);
                setQuoteError(errorMessage(error, 'Could not price this request'));
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [modalOpen, formData]);

    const openModal = () => {
        setFormData(EMPTY_FORM);
        setQuote(null);
        setQuoteError(null);
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.staff_id) {
            showToast('error', 'Validation Error', 'Pick a staff member');
            return;
        }
        const payload = {
            staff_id: formData.staff_id,
            request_type: formData.request_type,
            leave_category: formData.request_type === 'leave' ? formData.leave_category : null,
            from_date: formData.from_date,
            to_date: formData.request_type === 'permission' ? formData.from_date : formData.to_date,
            from_time: formData.request_type === 'permission' ? formData.from_time : null,
            to_time: formData.request_type === 'permission' ? formData.to_time : null,
            is_half_day: formData.is_half_day,
            reason: formData.reason || null,
        };
        try {
            setSaving(true);
            await CustomAxios.post('staff/requests/create/', payload);
            showToast('success', 'Submitted', 'Request created and sent for approval');
            setModalOpen(false);
            fetchRequests();
            fetchStats();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not submit request'));
        } finally {
            setSaving(false);
        }
    };

    const handleAction = async (item, action) => {
        const verb = { approve: 'Approve', reject: 'Reject', cancel: 'Cancel' }[action];
        let remarks = null;
        if (action !== 'approve') {
            remarks = window.prompt(`${verb} request #${item.id} — remarks (optional):`);
            if (remarks === null) return;
        } else if (!window.confirm(
            `Approve ${item.request_type_label} for ${item.staff_name} (${item.quantity_label})?\n\n` +
            `This deducts the balance and writes the attendance rows.`
        )) {
            return;
        }

        try {
            setSaving(true);
            await CustomAxios.put(`staff/requests/${item.id}/action/`, { action, remarks });
            showToast('success', 'Done', `Request ${action}d`);
            fetchRequests();
            fetchStats();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, `Could not ${action} the request`));
        } finally {
            setSaving(false);
        }
    };

    const pending = useMemo(() => requests.filter(r => r.status === 'pending'), [requests]);
    const decided = useMemo(() => requests.filter(r => r.status !== 'pending'), [requests]);

    const renderRow = (item, showActions) => (
        <TableRow key={item.id}>
            <TableCell sx={{ backgroundColor: 'white', fontFamily: 'monospace', fontSize: '11px', color: 'text.secondary' }}>
                REQ-{item.id}
            </TableCell>
            <TableCell sx={{ backgroundColor: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Avatar sx={{ width: 26, height: 26, fontSize: '10px', bgcolor: 'background.table', color: 'button.blue' }}>
                        {initialsOf(item.staff_name)}
                    </Avatar>
                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.staff_name}</Typography>
                        <Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>{item.employee_id}</Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell sx={{ backgroundColor: 'white' }}>
                <Chip
                    style={TYPE_STYLE[item.request_type] || TYPE_STYLE.leave}
                    label={item.request_type === 'leave' && item.leave_category
                        ? `Leave · ${item.leave_category.slice(0, 2).toUpperCase()}`
                        : item.request_type_label}
                />
            </TableCell>
            <TableCell sx={{ backgroundColor: 'white', whiteSpace: 'nowrap', fontSize: '12px' }}>{item.period_label}</TableCell>
            <TableCell sx={{ backgroundColor: 'white', textAlign: 'right', fontSize: '12px' }}>{item.quantity_label}</TableCell>
            <TableCell sx={{ backgroundColor: 'white', fontSize: '12px', maxWidth: '200px' }}>{item.reason || '—'}</TableCell>
            <TableCell sx={{ backgroundColor: 'white' }}>
                {item.is_paid
                    ? <Typography sx={{ fontSize: '11px', color: 'borders.green' }}>Paid</Typography>
                    : <Typography sx={{ fontSize: '11px', color: 'borders.red' }}>Unpaid / LOP</Typography>}
            </TableCell>
            <TableCell sx={{ backgroundColor: 'white' }}>
                <Chip style={STATUS_STYLE[item.status] || STATUS_STYLE.pending} label={item.status_label} />
            </TableCell>
            <TableCell sx={{ backgroundColor: 'white', whiteSpace: 'nowrap' }}>
                {showActions ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" variant="contained" disabled={saving}
                            onClick={() => handleAction(item, 'approve')} sx={{ fontSize: '11px', padding: '3px 10px' }}>
                            Approve
                        </Button>
                        <Button size="small" variant="outlined" color="error" disabled={saving}
                            onClick={() => handleAction(item, 'reject')} sx={{ fontSize: '11px', padding: '3px 10px' }}>
                            Reject
                        </Button>
                    </Box>
                ) : item.status === 'approved' && dayjs(item.from_date).isAfter(dayjs(), 'day') ? (
                    <Button size="small" variant="outlined" disabled={saving}
                        onClick={() => handleAction(item, 'cancel')} sx={{ fontSize: '11px', padding: '3px 10px' }}>
                        Cancel
                    </Button>
                ) : (
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        {item.actioned_by_name || '—'}
                    </Typography>
                )}
            </TableCell>
        </TableRow>
    );

    const headers = ['Ref', 'Staff', 'Type', 'When', 'Qty', 'Reason', 'Pay', 'Status', 'Action'];

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Typography variant="h5">Requests &amp; Approvals</Typography>
                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: '130px' }}>
                        <InputLabel>Type</InputLabel>
                        <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {REQUEST_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: '130px' }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ width: '240px' }}>
                        <Calendar value={range} onChange={(e) => setRange(e.value)} selectionMode="range"
                            readOnlyInput hideOnRangeSelection showIcon dateFormat="dd-mm-yy" style={{ width: '100%' }} />
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openModal}>New Request</Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', mb: 3 }}>
                <StatCard label="Pending" value={stats?.pending ?? '—'} hint="awaiting action" accent="#FA8C16" />
                <StatCard label="Approved" value={stats?.approved ?? '—'} hint="all time" accent="borders.green" />
                <StatCard label="Rejected" value={stats?.rejected ?? '—'} hint="all time" accent="borders.red" />
                <StatCard label="Cancelled" value={stats?.cancelled ?? '—'} hint="withdrawn" accent="secondary.border" />
            </Box>

            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', mb: 3 }}>
                <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                        Awaiting your action <Typography component="span" sx={{ fontWeight: 400, fontSize: '11px', color: 'text.secondary' }}>
                            · {pending.length} pending
                        </Typography>
                    </Typography>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            {headers.map((h, i) => (
                                <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', whiteSpace: 'nowrap' }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={9} sx={{ backgroundColor: 'white', height: '140px' }}>
                                {[0, 1].map(i => <Skeleton key={i} animation="wave" sx={{ height: '46px', background: 'rgb(0 0 0 / 6%)' }} />)}
                            </TableCell></TableRow>
                        ) : pending.length ? pending.map(item => renderRow(item, true)) : (
                            <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', backgroundColor: 'white', height: '140px', fontWeight: 700 }}>
                                Nothing pending
                            </TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012' }}>
                <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Decided</Typography>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            {headers.map((h, i) => (
                                <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', whiteSpace: 'nowrap' }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {decided.length ? decided.map(item => renderRow(item, false)) : (
                            <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', backgroundColor: 'white', height: '140px', fontWeight: 700 }}>
                                No decided requests in this range
                            </TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '92%', sm: 480 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 4,
                    borderRadius: 2, maxHeight: '90vh', overflow: 'auto'
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>New Request</Typography>

                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.06em', mb: 1 }}>
                        Request type
                    </Typography>
                    <ToggleButtonGroup
                        exclusive size="small" value={formData.request_type}
                        onChange={(e, value) => value && setFormData({ ...formData, request_type: value })}
                        sx={{ mb: 2, flexWrap: 'wrap' }}
                    >
                        {REQUEST_TYPES.map(t => (
                            <ToggleButton key={t.value} value={t.value} sx={{ px: 2, fontSize: '11px' }}>{t.label}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <FormControl fullWidth required sx={{ mb: 2 }}>
                        <InputLabel>Staff</InputLabel>
                        <Select value={formData.staff_id} label="Staff"
                            onChange={(e) => setFormData({ ...formData, staff_id: e.target.value })}>
                            {staffOptions.map(s => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {formData.request_type === 'leave' && (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Leave category</InputLabel>
                            <Select value={formData.leave_category} label="Leave category"
                                onChange={(e) => setFormData({ ...formData, leave_category: e.target.value })}>
                                {LEAVE_CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}

                    {formData.request_type === 'permission' ? (
                        <>
                            <TextField fullWidth label="Date" type="date" value={formData.from_date}
                                onChange={(e) => setFormData({ ...formData, from_date: e.target.value })}
                                InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <TextField fullWidth label="From" type="time" value={formData.from_time}
                                    onChange={(e) => setFormData({ ...formData, from_time: e.target.value })}
                                    InputLabelProps={{ shrink: true }} />
                                <TextField fullWidth label="To" type="time" value={formData.to_time}
                                    onChange={(e) => setFormData({ ...formData, to_time: e.target.value })}
                                    InputLabelProps={{ shrink: true }} />
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                <TextField fullWidth label="From date" type="date" value={formData.from_date}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        from_date: e.target.value,
                                        to_date: formData.to_date < e.target.value ? e.target.value : formData.to_date,
                                    })}
                                    InputLabelProps={{ shrink: true }} />
                                <TextField fullWidth label="To date" type="date" value={formData.to_date}
                                    disabled={formData.is_half_day}
                                    onChange={(e) => setFormData({ ...formData, to_date: e.target.value })}
                                    InputLabelProps={{ shrink: true }} />
                            </Box>
                            {formData.request_type === 'leave' && (
                                <FormControlLabel
                                    sx={{ mb: 1 }}
                                    control={<Checkbox checked={formData.is_half_day}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            is_half_day: e.target.checked,
                                            to_date: e.target.checked ? formData.from_date : formData.to_date,
                                        })} />}
                                    label={<Typography sx={{ fontSize: '13px' }}>Half day</Typography>}
                                />
                            )}
                        </>
                    )}

                    {quote && (
                        <Alert severity={quote.is_paid ? 'success' : 'warning'} sx={{ mb: 2, fontSize: '12px' }}>
                            {quote.note}
                        </Alert>
                    )}
                    {quoteError && (
                        <Alert severity="error" sx={{ mb: 2, fontSize: '12px' }}>{quoteError}</Alert>
                    )}

                    <TextField fullWidth multiline rows={2} label="Reason" value={formData.reason}
                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })} sx={{ mb: 2 }} />

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={saving || Boolean(quoteError)}>
                            Submit
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
